/**
 * De omvang achter een schakelaar, in woorden. R47 stap 2.
 *
 * WAAROM DIT EEN EIGEN BESTAND IS EN GEEN REGEL IN EEN `useEffect`. De
 * schakelaarregels worden sinds 22 augustus 2026 op twee manieren gevuld: per
 * PROJECT wanneer er geen project is gekozen, en per BANK daarbinnen. De tekst
 * verschilt dus per geval, en zij hoort niet in de ophaalcode te staan waar
 * niemand haar los kan toetsen. Dit is de vorm die CLAUDE.md 4.8c voorschrijft:
 * trek de beslissing uit de moeilijk toetsbare context naar een pure functie en
 * toets die op UITKOMST.
 *
 * DE OMVANG ERBIJ IS DE HELE REDEN VAN DE SCHAKELAAR. Een winkel gaat in zijn
 * GEHEEL het geheugen in zodra er een keer in wordt gezocht; de grootste is
 * 41,1 MB. Wie iets aanzet hoort te zien wat hij aanzet.
 */

function meervoud(aantal, enkel, meer) {
  return `${aantal} ${aantal === 1 ? enkel : meer}`;
}

/**
 * `2 banken, 8 documenten`. Voor een projectregel.
 *
 * ALTIJD BEIDE GETALLEN, ook wanneer er een bank is. "DOQENT, 1 bank,
 * 206 documenten" leest raar maar liegt niet; het aantal banken weglaten zou
 * verbergen dat een klik er meer dan een aanzet.
 */
export function labelVoorProject(project) {
  const banken = project?.bank_count || 0;
  const documenten = project?.document_count || 0;
  return `${meervoud(banken, "bank", "banken")}, `
       + `${meervoud(documenten, "document", "documenten")}`;
}

/**
 * `402 bestanden`, of een lege tekst als er niets te melden valt.
 *
 * LEEG BIJ NUL, en dat is met opzet anders dan hierboven. Een bankregel die
 * "0 bestanden" toont, meldt iets wat de gebruiker niet kan gebruiken; een
 * projectregel zonder banken bestaat niet, want die komt niet in de lijst.
 */
export function labelVoorBank(bank) {
  const aantal = bank?.document_count || 0;
  return aantal > 0 ? meervoud(aantal, "bestand", "bestanden") : "";
}

/**
 * Waar de regels vandaan komen, en dus welk woord de knop hoort te gebruiken.
 *
 * WAAROM DIT EEN FUNCTIE IS EN GEEN TERNARY IN HET SCHERM. Als vraagteken in de
 * JSX van `ChatPage` is deze keuze alleen met een bronscan te toetsen, en dan
 * bewijst een groene toets dat de REGEL er staat en niet dat zij het juiste
 * antwoord geeft. Dat is de situatie die CLAUDE.md 4.8c beschrijft: trek de
 * beslissing uit de moeilijk toetsbare context naar een pure functie en toets
 * die op uitkomst. De bronscan bewijst dan nog alleen de bedrading, en dat is
 * het enige waar hij goed in is.
 */
export function soortVanRegels(projectId) {
  return projectId ? "bank" : "project";
}

/**
 * Wat er OP de keuzeknop staat, en wat de zweeftekst voluit meldt.
 *
 * TOEGEVOEGD OP 23 AUGUSTUS 2026, FRANKS OPDRACHT. De pillenbalk duwde met 18
 * regels over acht regels het scherm weg. De schakelaars verhuizen naar een
 * uitklapmenu, en dan is de vraag wat er op de knop komt: hij wil kunnen zien
 * DAT er iets meedoet zonder te openen.
 *
 * VARIANT A, ZIJN KEUZE UIT DRIE. Bij precies EEN aangezet project staat de
 * NAAM er en niet het cijfer `1`, want dat is de stand die je het vaakst hebt en
 * `DOQENT` zegt strikt meer dan `1`. Vanaf twee is de naam niet meer te doen en
 * telt het aantal.
 *
 * EEN HALF AANGEZET PROJECT TELT HIER ALS MEEDOEND, en dat is het punt waar
 * deze functie afwijkt van de oude pil. Die keek met `every` en toonde half aan
 * als UIT. Maar half aan betekent dat er wel degelijk een bank meegaat in
 * `include_bases`, dus een knop die dan "geen project doet mee" meldt, liegt
 * over wat er aan de vraag wordt toegevoegd. Dat is dezelfde klasse als de
 * dode `document_count`-tak: een waarde die klopt en nergens uitkomt, hier in
 * de omgekeerde richting.
 *
 * Het VERSCHIL tussen half en heel verdwijnt daarmee niet, het verhuist: de
 * zweeftekst zet `(half)` achter de naam en het menu toont een streepje in
 * plaats van een vinkje.
 *
 * DE WOORDEN ZIJN EEN PARAMETER EN GEEN AFLEIDING. Zonder gekozen project zijn
 * de regels PROJECTEN, daarbinnen zijn het BANKEN. Dat is aan de gegevens niet
 * te zien: `Nice Tea` is een project met precies een bank, en die regel ziet er
 * identiek uit als een bankregel. Wie het zou afleiden uit `base_ids.length`
 * zou dus bij elk klein project het verkeerde woord tonen.
 */
export function samenvatting(bases, on,
                             woorden = { enkel: "project", meer: "projecten" }) {
  const aan = new Set(on || []);

  const meedoend = (bases || []).map((regel) => {
    // DEZELFDE TERUGVAL ALS IN DE COMPONENT: een regel zonder `base_ids` draagt
    // zichzelf. Er is bewust GEEN gordel op een lege lijst, want die kan niet
    // ontstaan: `base_ids: []` valt terug op `[regel.id]` en dat is er altijd
    // een. Een gordel die niet kan afgaan suggereert dekking die er niet is.
    const ids = regel?.base_ids?.length ? regel.base_ids : [regel?.id];
    const mee = ids.filter((id) => aan.has(id)).length;
    return { naam: regel?.name || "", mee, totaal: ids.length };
  }).filter((regel) => regel.mee > 0);

  const aantal = meedoend.length;

  const tekst = aantal === 0 ? ""
              : aantal === 1 ? meedoend[0].naam
              : `${aantal} ${woorden.meer}`;

  const namen = meedoend.map((regel) =>
    regel.mee < regel.totaal ? `${regel.naam} (half)` : regel.naam);

  const toelichting = aantal === 0
    ? `Geen ${woorden.enkel} doet mee`
    : `${meervoud(aantal, woorden.enkel, woorden.meer)} `
      + `${aantal === 1 ? "doet" : "doen"} mee: ${namen.join(", ")}`;

  return { aantal, tekst, toelichting };
}
